// V2.1A Step C — Dettaglio SAL: azioni transizione + tabella verifica
// V2.1A Step D.1 — Aggiunto tab "Documenti" (visibile solo con feature flag ON).
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from '../ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { salApi, SAL_STATUS_LABELS, SAL_STATUS_TONE, formatEuro, formatQty, extractApiError } from './salApi';
import { useSubcontractSalV2Enabled } from './SubcontractSALTab';
import SubcontractSALDocumentsPanel from './SubcontractSALDocumentsPanel';

const ACTIONS_BY_STATUS = {
  bozza: [
    { target: 'presentato', label: 'Presenta', tone: 'default' },
    { target: 'annullato', label: 'Annulla', tone: 'destructive', reasonRequired: true },
  ],
  presentato: [
    { target: 'in_verifica', label: 'Avvia verifica', tone: 'default' },
    { target: 'bozza', label: 'Torna in bozza', tone: 'outline', reasonRequired: true },
    { target: 'annullato', label: 'Annulla', tone: 'destructive', reasonRequired: true },
  ],
  in_verifica: [
    { target: 'approvato', label: 'Approva', tone: 'default', confirm: 'approve' },
    { target: 'contestato', label: 'Contesta', tone: 'outline', reasonRequired: true },
    { target: 'bozza', label: 'Torna in bozza', tone: 'outline', reasonRequired: true },
    { target: 'annullato', label: 'Annulla', tone: 'destructive', reasonRequired: true },
  ],
  contestato: [
    { target: 'in_verifica', label: 'Riprendi verifica', tone: 'default' },
    { target: 'bozza', label: 'Torna in bozza', tone: 'outline', reasonRequired: true },
    { target: 'annullato', label: 'Annulla', tone: 'destructive', reasonRequired: true },
  ],
  approvato: [
    { target: 'autorizzato_fattura', label: 'Autorizza fatturazione', tone: 'default', confirm: 'authorize' },
  ],
  autorizzato_fattura: [],
  annullato: [],
};

const SubcontractSALDetail = ({ open, onClose, sal, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [reason, setReason] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const docsEnabled = useSubcontractSalV2Enabled();

  if (!sal) return null;
  const actions = ACTIONS_BY_STATUS[sal.status] || [];
  const revision = sal.revision;

  const doTransition = async (targetStatus, providedReason) => {
    setBusy(true);
    try {
      const r = await salApi.transition(sal.id, {
        target_status: targetStatus,
        reason: providedReason || null,
        expected_revision: revision,
      });
      toast.success(`SAL ${SAL_STATUS_LABELS[targetStatus]}`);
      onChanged(r.data);
      setConfirmAction(null);
      setReason('');
    } catch (e) {
      const info = extractApiError(e);
      if (info.code === 'revision_conflict') {
        toast.error('Il SAL è stato modificato da un altro utente. Ricarica i dati.');
      } else {
        toast.error(info.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const onItemVerified = async (itemId, patch) => {
    setBusy(true);
    try {
      const r = await salApi.itemVerification(sal.id, itemId, {
        ...patch,
        expected_revision: revision,
      });
      onChanged(r.data);
      toast.success('Riga aggiornata');
    } catch (e) {
      toast.error(extractApiError(e).message);
    } finally {
      setBusy(false);
    }
  };

  const showItemsTable = ['in_verifica', 'contestato'].includes(sal.status);
  const readonly = ['approvato', 'autorizzato_fattura', 'annullato'].includes(sal.status);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="sal-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {sal.sal_number}
            <Badge className={`${SAL_STATUS_TONE[sal.status]} border`} data-testid="sal-detail-status-badge">
              {SAL_STATUS_LABELS[sal.status]}
            </Badge>
            {sal.is_final_sal && <Badge className="bg-amber-100 text-amber-800 border border-amber-300">SAL FINALE</Badge>}
            <span className="text-xs text-neutral-500 ml-auto">rev {sal.revision}</span>
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2">
          <TabsList data-testid="sal-detail-tabs">
            <TabsTrigger value="overview" data-testid="sal-detail-tab-overview">Panoramica</TabsTrigger>
            {docsEnabled && (
              <TabsTrigger value="documents" data-testid="sal-detail-tab-documents">Documenti</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="overview" className="space-y-3 mt-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="Richiesto" value={formatEuro(sal.gross_requested_amount)} />
              <Kpi label="Approvato" value={formatEuro(sal.gross_approved_amount)} tone="text-emerald-700" />
              <Kpi label="Ritenuta" value={formatEuro(sal.retention_amount)} tone="text-amber-700" />
              <Kpi label="Netto autorizzato" value={formatEuro(sal.authorized_net_amount)} tone="text-teal-700" />
            </div>

            <div className="text-xs text-neutral-600 space-y-0.5 pt-2">
              {sal.description && <div><b>Descrizione:</b> {sal.description}</div>}
              <div><b>Periodo:</b> {sal.reference_period_from || '—'} → {sal.reference_period_to || '—'}</div>
              <div><b>Ritenuta snapshot:</b> {sal.retention_percent_snapshot}%</div>
              {sal.submitted_at && <div><b>Presentato:</b> {new Date(sal.submitted_at).toLocaleString('it-IT')} da {sal.submitted_by}</div>}
              {sal.approved_at && <div><b>Approvato:</b> {new Date(sal.approved_at).toLocaleString('it-IT')} da {sal.approved_by}</div>}
              {sal.cancelled_at && <div><b>Annullato:</b> {new Date(sal.cancelled_at).toLocaleString('it-IT')} · Motivo: {sal.cancellation_reason}</div>}
              {sal.contestation_reason && <div><b>Motivo contestazione:</b> {sal.contestation_reason}</div>}
            </div>

            <div className="border border-neutral-200 rounded overflow-x-auto mt-3">
              <table className="w-full text-sm" data-testid="sal-detail-items-table">
                <thead className="bg-neutral-50 text-xs uppercase text-neutral-600">
                  <tr>
                    <th className="text-left px-3 py-2">Codice</th>
                    <th className="text-left px-3 py-2">Descrizione</th>
                    <th className="text-right px-3 py-2">Richiesta</th>
                    <th className="text-right px-3 py-2">Approvata</th>
                    <th className="text-right px-3 py-2">Cumulata</th>
                    <th className="text-right px-3 py-2">Residua</th>
                    <th className="text-right px-3 py-2">Importo</th>
                    {showItemsTable && !readonly && <th className="text-left px-3 py-2">Note contest.</th>}
                  </tr>
                </thead>
                <tbody>
                  {(sal.items || []).map(it => (
                    <ItemRow
                      key={it.id}
                      it={it}
                      editable={showItemsTable && !readonly}
                      onSave={(patch) => onItemVerified(it.id, patch)}
                      busy={busy}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {docsEnabled && (
            <TabsContent value="documents" className="mt-3" data-testid="sal-detail-tab-documents-content">
              <SubcontractSALDocumentsPanel sal={sal} onChanged={() => onChanged(sal)} />
            </TabsContent>
          )}
        </Tabs>

        <DialogFooter className="flex flex-wrap gap-2 justify-between mt-4">
          <Button variant="outline" onClick={onClose} data-testid="sal-detail-close">Chiudi</Button>
          <div className="flex flex-wrap gap-2">
            {actions.map(a => (
              <Button
                key={a.target}
                variant={a.tone === 'destructive' ? 'destructive' : a.tone === 'outline' ? 'outline' : 'default'}
                onClick={() => {
                  if (a.reasonRequired || a.confirm) {
                    setConfirmAction(a);
                    setReason('');
                  } else {
                    doTransition(a.target);
                  }
                }}
                disabled={busy}
                data-testid={`sal-action-${a.target}`}
              >
                {a.label}
              </Button>
            ))}
          </div>
        </DialogFooter>

        <AlertDialog open={!!confirmAction} onOpenChange={(v) => !v && setConfirmAction(null)}>
          <AlertDialogContent data-testid="sal-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>
                {confirmAction?.confirm === 'approve' && 'Confermi l&apos;approvazione del SAL?'}
                {confirmAction?.confirm === 'authorize' && 'Confermi l&apos;autorizzazione fatturazione?'}
                {confirmAction?.reasonRequired && `${confirmAction.label} — inserisci motivo`}
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div>
                  {confirmAction?.confirm === 'approve' && (
                    <span className="block mb-3">
                      Con l&apos;approvazione, le quantità approvate diventeranno autoritative e concorreranno al cumulato del Subappalto.
                      <br /><br />
                      <b>Importo netto autorizzato:</b> {formatEuro(sal.authorized_net_amount)}
                    </span>
                  )}
                  {confirmAction?.confirm === 'authorize' && (
                    <span className="block mb-3">
                      SAL approvato per <b>{formatEuro(sal.authorized_net_amount)}</b> netto. Verrà autorizzato alla fatturazione futura.
                    </span>
                  )}
                  {confirmAction?.reasonRequired && (
                    <div className="pt-2">
                      <Label>Motivo *</Label>
                      <Textarea
                        rows={3}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        data-testid="sal-confirm-reason"
                      />
                    </div>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="sal-confirm-cancel">Annulla</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => doTransition(confirmAction.target, confirmAction.reasonRequired ? reason : null)}
                disabled={confirmAction?.reasonRequired && !reason.trim()}
                data-testid="sal-confirm-ok"
              >
                Conferma
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};

const Kpi = ({ label, value, tone = 'text-neutral-900' }) => (
  <div className="border border-neutral-200 rounded p-2">
    <div className="text-[10px] uppercase text-neutral-500">{label}</div>
    <div className={`text-sm font-semibold ${tone}`}>{value}</div>
  </div>
);

const ItemRow = ({ it, editable, onSave, busy }) => {
  const [approved, setApproved] = useState(String(it.approved_quantity ?? ''));
  const [notes, setNotes] = useState(it.contestation_notes || '');
  const dirty = String(it.approved_quantity ?? '') !== approved || (it.contestation_notes || '') !== notes;
  const excess = Number(approved) > Number(it.requested_quantity || 0);

  return (
    <tr className="border-t border-neutral-100">
      <td className="px-3 py-2 font-medium">{it.code_snapshot || '—'}</td>
      <td className="px-3 py-2 max-w-[260px] truncate">{it.description_snapshot}</td>
      <td className="px-3 py-2 text-right tabular-nums">{formatQty(it.requested_quantity)}</td>
      <td className="px-3 py-2 text-right tabular-nums">
        {editable ? (
          <Input
            type="number"
            step="0.001"
            min="0"
            max={it.requested_quantity}
            value={approved}
            onChange={(e) => setApproved(e.target.value)}
            className={`w-24 text-right ${excess ? 'border-rose-500' : ''}`}
            data-testid={`sal-detail-approved-${it.code_snapshot || it.id}`}
          />
        ) : (
          formatQty(it.approved_quantity)
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">{formatQty(it.cumulative_quantity)}</td>
      <td className="px-3 py-2 text-right tabular-nums">{formatQty(it.residual_quantity)}</td>
      <td className="px-3 py-2 text-right tabular-nums font-medium">{formatEuro(it.approved_amount)}</td>
      {editable && (
        <td className="px-3 py-2">
          <div className="flex gap-2">
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Note verifica"
              className="min-w-[140px]"
              data-testid={`sal-detail-notes-${it.code_snapshot || it.id}`}
            />
            {dirty && (
              <Button
                size="sm"
                onClick={() => onSave({ approved_quantity: Number(approved), contestation_notes: notes })}
                disabled={busy || excess}
                data-testid={`sal-detail-save-item-${it.code_snapshot || it.id}`}
              >
                Salva
              </Button>
            )}
          </div>
        </td>
      )}
    </tr>
  );
};

export default SubcontractSALDetail;
